import { Component } from 'react';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, ScaleDirection } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { appHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { withChartLogic } from 'components/common';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import { FormField, SectionTitle } from './constants';
import HumptyDumptyFallsScaleView, { HumptyDumptyFallsScaleViewProps } from './HumptyDumptyFallsScaleView';

class HumptyDumptyFallsScale extends Component<ChartComponentProps> {
  static displayName = 'HumptyDumptyFallsScale';

  componentDidMount() {
    this.props.initState(this.buildDefaultFormFields());
  }

  buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
    const { createFormField } = chartHelper;
    const dataMap = new Map();

    dataMap.set(
      FormField.HUMPTY_DUMPTY_FALLS_SCALE,
      createFormField({ name: FormField.HUMPTY_DUMPTY_FALLS_SCALE, type: FormFieldInputType.SCALE, errorLabel: 'Humpty Dumpty Falls Scale' })
    );

    return dataMap;
  };

  getContextDirectionMap = (): Map<string, ScaleDirection> => {
    const contextDirectionMap = new Map();
    contextDirectionMap.set(FormField.AGE, ScaleDirection.VERTICALLY);
    contextDirectionMap.set(FormField.SEX, ScaleDirection.VERTICALLY);
    contextDirectionMap.set(FormField.DIAGNOSIS, ScaleDirection.VERTICALLY);
    contextDirectionMap.set(FormField.COGNITIVE_IMPAIRMENTS, ScaleDirection.VERTICALLY);
    contextDirectionMap.set(FormField.ENVIRONMENTAL_FACTORS, ScaleDirection.VERTICALLY);
    contextDirectionMap.set(FormField.RESPONSE_TO_SURGERY, ScaleDirection.VERTICALLY);
    contextDirectionMap.set(FormField.MEDICATION_USAGE, ScaleDirection.VERTICALLY);
    return contextDirectionMap;
  };

  handleSaveClick = () => {
    this.props.saveChart([this.buildFragment()], { defaultFormFields: this.buildDefaultFormFields(), afterSave: this.props.navigateToSavedPatientCharting });
  };

  buildFragment = () => {
    const { buildPatientRecords, buildPlainRecord } = chartService;
    const { formFieldMap } = this.props;

    const record = {
      chartTitle: 'Special Charts',
      fragmentTitle: SectionTitle.HUMPTY_DUMPTY_FALLS_SCALE,
      records: [
        {
          sectionTitle: SectionTitle.HUMPTY_DUMPTY_FALLS_SCALE,
          records: [...buildPatientRecords(formFieldMap, FormField.HUMPTY_DUMPTY_FALLS_SCALE, false, true)]
        },
        {
          sectionTitle: SectionTitle.TOTAL_SCORE,
          records: [
            buildPlainRecord({
              formField: FormField.HUMPTY_DUMPTY_FALLS_SCALE,
              title: 'Total Score',
              content: chartHelper.getScaleRollUpWrapper(formFieldMap.get(FormField.HUMPTY_DUMPTY_FALLS_SCALE))?.score?.value,
              linkedFormFieldIds: [
                FormField.AGE,
                FormField.SEX,
                FormField.DIAGNOSIS,
                FormField.COGNITIVE_IMPAIRMENTS,
                FormField.ENVIRONMENTAL_FACTORS,
                FormField.RESPONSE_TO_SURGERY,
                FormField.MEDICATION_USAGE
              ]
            })
          ]
        }
      ]
    };

    // update Medication Usage record title
    record.records
      .find((sectionRecord) => sectionRecord.sectionTitle === SectionTitle.HUMPTY_DUMPTY_FALLS_SCALE)
      .records.find((item) => item.formField === FormField.MEDICATION_USAGE).title = 'Medication Usage';
    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const basicInfo = chartService.createBaseFragment({ chartingTime: this.props.chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  };

  handleCancelClick = () => {
    this.props.initState(this.buildDefaultFormFields());
    appHelper.scrollTop();
  };

  render() {
    const chartActionsProps: ChartActionsComponentProps = {
      onSaveClick: this.handleSaveClick,
      onCancelClick: () => this.props.handleDiscardClick(this.handleCancelClick),
      onDisplayRecordsClick: this.props.displayAuthoringData,
      enableSaveButton: this.props.hasUnsavedChanges,
      enableDisplayRecordsButton: this.props.enableDisplayRecordsButton
    };

    const viewProps: HumptyDumptyFallsScaleViewProps = {
      chartActionsProps,
      formFieldMap: this.props.formFieldMap,
      formSubmittedCount: this.props.formSubmittedCount,
      contextDirectionMap: this.getContextDirectionMap()
    };

    return <HumptyDumptyFallsScaleView {...viewProps} />;
  }
}

export { HumptyDumptyFallsScale as BaseHumptyDumptyFallsScale };
export default compose(withSavedPatientChartsPage, withChartLogic)(HumptyDumptyFallsScale);
